"use client";

import type { ReactNode } from "react";
import { CopyButton } from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { downloadText } from "@/lib/tool-kit/download";
import type {
  DocBlock,
  DocModel,
} from "@/lib/tool-kit/research-workflow/document";

/*
 * The parts the research-workflow document generators share: the on-page rendering of a
 * `DocModel` and the row of copy and download buttons under it. Every word comes from
 * the calling widget's strings through props.
 */

function Block({ block }: { block: DocBlock }) {
  if (block.kind === "p") {
    return <p>{block.text}</p>;
  }
  if (block.kind === "h") {
    return <h5 className="pt-1 font-medium text-foreground">{block.text}</h5>;
  }
  if (block.kind === "fields") {
    return (
      <div className="space-y-3 pt-1">
        {block.items.map((item) => (
          <p className="break-words font-mono text-xs sm:text-sm" key={item}>
            {item}
          </p>
        ))}
      </div>
    );
  }
  const items = block.items.map((item, index) => (
    // Generated list items can repeat (two identical prompts); position is identity.
    // biome-ignore lint/suspicious/noArrayIndexKey: positional list items
    <li key={index}>{item}</li>
  ));
  return block.kind === "ol" ? (
    <ol className="list-decimal space-y-1 pl-6" start={block.start}>
      {items}
    </ol>
  ) : (
    <ul className="list-disc space-y-1 pl-6">{items}</ul>
  );
}

/** A generated document, drawn as a page: title, meta lines, headed sections. */
export function DocumentPreview({
  doc,
  label,
}: {
  doc: DocModel;
  /** The preview's accessible name, e.g. "Consent form preview". */
  label: string;
}) {
  return (
    <article
      aria-label={label}
      className="space-y-5 rounded-lg border border-border bg-card p-5 text-foreground text-sm leading-relaxed sm:p-8"
    >
      <header className="space-y-2">
        <h3 className="font-semibold text-lg">{doc.title}</h3>
        {doc.meta.length > 0 ? (
          <p className="text-muted-foreground">
            {doc.meta.map((line, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: positional meta lines
              <span className="block" key={index}>
                {line}
              </span>
            ))}
          </p>
        ) : null}
      </header>
      {doc.sections.map((section, sectionIndex) => (
        // Sections are positional: two can share a heading, and the order is fixed.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional sections
        <section className="space-y-2" key={sectionIndex}>
          {section.heading ? (
            <h4 className="font-semibold text-base">{section.heading}</h4>
          ) : null}
          {section.blocks.map((block, blockIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional blocks
            <Block block={block} key={blockIndex} />
          ))}
        </section>
      ))}
    </article>
  );
}

export interface DocumentActionLabels {
  copyText: string;
  copyFormatted: string;
  copied: string;
  downloadText: string;
  downloadMarkdown?: string;
}

/** Copy as text, copy with formatting for Word, and download as a file. */
export function DocumentActions({
  text,
  html,
  markdown,
  filename,
  labels,
  children,
}: {
  text: string;
  html: string;
  markdown?: string;
  /** Without an extension: `consent-form` → `consent-form.txt`. */
  filename: string;
  labels: DocumentActionLabels;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      <CopyButton
        copiedLabel={labels.copied}
        html={html}
        label={labels.copyFormatted}
        text={text}
      />
      <CopyButton
        copiedLabel={labels.copied}
        label={labels.copyText}
        text={text}
      />
      <button
        className={BUTTON_SECONDARY}
        onClick={() => downloadText(`${filename}.txt`, text)}
        type="button"
      >
        {labels.downloadText}
      </button>
      {markdown && labels.downloadMarkdown ? (
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadText(
              `${filename}.md`,
              markdown,
              "text/markdown;charset=utf-8"
            )
          }
          type="button"
        >
          {labels.downloadMarkdown}
        </button>
      ) : null}
      {children}
    </div>
  );
}
